<script>
import { mapGetters } from 'vuex';
import { useAutomation } from 'dashboard/composables/useAutomation';
import { useEditableAutomation } from 'dashboard/composables/useEditableAutomation';
import FilterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import {
  getFileName,
  generateAutomationPayload,
  getAttributes,
  getInputType,
  getOperators,
  getCustomAttributeType,
  showActionInput,
} from 'dashboard/helper/automationHelper';
import { validateAutomation } from 'dashboard/helper/validations';

import { AUTOMATION_RULE_EVENTS, AUTOMATION_ACTION_TYPES } from './constants';

export default {
  components: {
    FilterInputBox,
    NextButton,
    AutomationActionInput,
  },
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    selectedResponse: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['saveAutomation'],
  setup() {
    const {
      automation,
      automationTypes,
      onEventChange,
      getConditionDropdownValues,
      appendNewCondition,
      appendNewAction,
      removeFilter,
      removeAction,
      resetFilter,
      resetAction,
      getActionDropdownValues,
      manifestCustomAttributes,
    } = useAutomation();
    const { formatAutomation } = useEditableAutomation();
    return {
      automation,
      automationTypes,
      onEventChange,
      getConditionDropdownValues,
      appendNewCondition,
      appendNewAction,
      removeFilter,
      removeAction,
      resetFilter,
      resetAction,
      getActionDropdownValues,
      formatAutomation,
      manifestCustomAttributes,
    };
  },
  data() {
    return {
      automationRuleEvent: AUTOMATION_RULE_EVENTS[0].key,
      automationMutated: false,
      show: true,
      showDeleteConfirmationModal: false,
      allCustomAttributes: [],
      mode: 'edit',
      errors: {},
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    automationRuleEvents() {
      return AUTOMATION_RULE_EVENTS.map(event => ({
        ...event,
        value: this.$t(`AUTOMATION.EVENTS.${event.value}`),
      }));
    },
    hasAutomationMutated() {
      if (
        this.automation.conditions[0].values ||
        this.automation.actions[0].action_params.length
      )
        return true;
      return false;
    },
    automationActionTypes() {
      const actionTypes = this.isFeatureEnabled('sla')
        ? AUTOMATION_ACTION_TYPES
        : AUTOMATION_ACTION_TYPES.filter(({ key }) => key !== 'add_sla');

      return actionTypes.map(action => ({
        ...action,
        label: this.$t(`AUTOMATION.ACTIONS.${action.label}`),
      }));
    },
  },
  mounted() {
    this.manifestCustomAttributes();
    this.allCustomAttributes = this.$store.getters['attributes/getAttributes'];

    this.automation = this.formatAutomation(
      this.selectedResponse,
      this.allCustomAttributes,
      this.automationTypes,
      this.automationActionTypes
    );
  },
  methods: {
    getFileName,
    getAttributes,
    getInputType,
    getOperators,
    getCustomAttributeType,
    showActionInput,
    isFeatureEnabled(flag) {
      return this.isFeatureEnabledonAccount(this.accountId, flag);
    },
    emitSaveAutomation() {
      this.errors = validateAutomation(this.automation);
      if (Object.keys(this.errors).length === 0) {
        const automation = generateAutomationPayload(this.automation);
        this.$emit('saveAutomation', automation, this.mode);
      }
    },
    getTranslatedAttributes(type, event) {
      return getAttributes(type, event).map(attribute => {
        // Skip translation
        // 1. If customAttributeType key is present then its rendering attributes from API
        // 2. If contact_custom_attribute or conversation_custom_attribute is present then its rendering section title
        const skipTranslation =
          attribute.customAttributeType ||
          [
            'contact_custom_attribute',
            'conversation_custom_attribute',
          ].includes(attribute.key);

        return {
          ...attribute,
          name: skipTranslation
            ? attribute.name
            : this.$t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
        };
      });
    },
  },
};
</script>

<template>
  <div class="automation-modal">
    <woot-modal-header :header-title="$t('AUTOMATION.EDIT.TITLE')" />
    <div class="modal-content">
      <div v-if="automation" class="automation-modal__body">
        <div class="automation-modal__intro">
          <p class="automation-modal__eyebrow">Automation</p>
          <p class="automation-modal__subtitle">
            Update the trigger, conditions, and actions without changing the
            rule behavior flow.
          </p>
        </div>

        <div class="automation-modal__grid">
          <woot-input
            v-model="automation.name"
            :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
            type="text"
            :class="{ error: errors.name }"
            :error="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
            :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
          />
          <woot-input
            v-model="automation.description"
            :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
            type="text"
            :class="{ error: errors.description }"
            :error="
              errors.description ? $t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''
            "
            :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
          />
        </div>

        <section class="automation-modal__section automation-modal__section--event">
          <div class="automation-modal__section-head">
            <div>
              <p class="automation-modal__section-kicker">Trigger</p>
              <h3 class="automation-modal__section-title">
                {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
              </h3>
            </div>
          </div>

          <label
            class="automation-modal__field"
            :class="{ error: errors.event_name }"
          >
            <select
              v-model="automation.event_name"
              class="automation-modal__select"
              @change="onEventChange(automation)"
            >
              <option
                v-for="event in automationRuleEvents"
                :key="event.key"
                :value="event.key"
              >
                {{ event.value }}
              </option>
            </select>
            <span v-if="errors.event_name" class="message">
              {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
            </span>
          </label>
        </section>

        <section class="automation-modal__section">
          <div class="automation-modal__section-head">
            <div>
              <p class="automation-modal__section-kicker">Logic</p>
              <h3 class="automation-modal__section-title">
                {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
              </h3>
            </div>
            <span class="automation-modal__badge">
              {{ automation.conditions.length }}
            </span>
          </div>

          <div class="automation-modal__card">
            <FilterInputBox
              v-for="(condition, i) in automation.conditions"
              :key="i"
              v-model="automation.conditions[i]"
              :filter-attributes="
                getTranslatedAttributes(automationTypes, automation.event_name)
              "
              :input-type="
                getInputType(
                  allCustomAttributes,
                  automationTypes,
                  automation,
                  automation.conditions[i].attribute_key
                )
              "
              :operators="
                getOperators(
                  allCustomAttributes,
                  automationTypes,
                  automation,
                  mode,
                  automation.conditions[i].attribute_key
                )
              "
              :dropdown-values="
                getConditionDropdownValues(
                  automation.conditions[i].attribute_key
                )
              "
              :custom-attribute-type="
                getCustomAttributeType(
                  automationTypes,
                  automation,
                  automation.conditions[i].attribute_key
                )
              "
              :show-query-operator="i !== automation.conditions.length - 1"
              :error-message="
                errors[`condition_${i}`]
                  ? $t(`AUTOMATION.ERRORS.${errors[`condition_${i}`]}`)
                  : ''
              "
              @reset-filter="resetFilter(i, automation.conditions[i])"
              @remove-filter="removeFilter(i)"
            />
            <div class="automation-modal__add-row">
              <NextButton
                icon="i-lucide-plus"
                orange
                faded
                sm
                :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
                @click="appendNewCondition"
              />
            </div>
          </div>
        </section>

        <section class="automation-modal__section">
          <div class="automation-modal__section-head">
            <div>
              <p class="automation-modal__section-kicker">Outcome</p>
              <h3 class="automation-modal__section-title">
                {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
              </h3>
            </div>
            <span class="automation-modal__badge">
              {{ automation.actions.length }}
            </span>
          </div>

          <div class="automation-modal__card">
            <AutomationActionInput
              v-for="(action, i) in automation.actions"
              :key="i"
              v-model="automation.actions[i]"
              :action-types="automationActionTypes"
              :dropdown-values="getActionDropdownValues(action.action_name)"
              :show-action-input="
                showActionInput(automationActionTypes, action.action_name)
              "
              :error-message="
                errors[`action_${i}`]
                  ? $t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`)
                  : ''
              "
              :initial-file-name="getFileName(action, automation.files)"
              @reset-action="resetAction(i)"
              @remove-action="removeAction(i)"
            />
            <div class="automation-modal__add-row">
              <NextButton
                icon="i-lucide-plus"
                orange
                faded
                sm
                :label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
                @click="appendNewAction"
              />
            </div>
          </div>
        </section>
      </div>

      <div class="automation-modal__footer">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('AUTOMATION.EDIT.CANCEL_BUTTON_TEXT')"
          @click.prevent="onClose"
        />
        <NextButton
          solid
          onyx
          type="submit"
          :label="$t('AUTOMATION.EDIT.SUBMIT')"
          @click="emitSaveAutomation"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
::deep(.modal-header) {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb !important;
}

::deep(.modal-content) {
  background: #ffffff !important;
  width: min(860px, 94vw) !important;
  max-width: min(860px, 94vw) !important;
}

::deep(label) {
  color: #111827 !important;
}

::deep(input:not(.reset-base)),
::deep(select:not(.reset-base)),
::deep(textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}

::deep(.filter) {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}

::deep(.bg-n-slate-2),
::deep(.bg-n-solid-2),
::deep(.bg-n-background) {
  background: #f9fafb !important;
}

::deep(.border-n-strong),
::deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}

::deep(.text-n-slate-12) {
  color: #111827 !important;
}

::deep(.text-n-slate-11) {
  color: #4b5563 !important;
}

.automation-modal__body {
  padding: 24px 24px 8px;
}

.automation-modal__intro {
  margin-bottom: 20px;
}

.automation-modal__eyebrow {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9ca3af;
}

.automation-modal__subtitle {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #6b7280;
}

.automation-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.automation-modal__section {
  margin-bottom: 20px;
}

.automation-modal__section--event {
  padding: 18px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
}

.automation-modal__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.automation-modal__section-kicker {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9ca3af;
}

.automation-modal__section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.automation-modal__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
}

.automation-modal__field {
  display: block;
}

.automation-modal__select {
  width: 100%;
  min-height: 44px;
  border-radius: 10px;
}

.automation-modal__card {
  width: 100%;
  padding: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #f9fafb;
}

.automation-modal__add-row {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed #d1d5db;
}

.automation-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 24px;
  border-top: 1px solid #f3f4f6;
  background: #ffffff;
}

@media (max-width: 768px) {
  .automation-modal__body {
    padding: 20px 16px 8px;
  }

  .automation-modal__grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .automation-modal__footer {
    padding: 16px;
    flex-wrap: wrap;
  }
}
</style>
